import React, { useState, useRef, useEffect } from 'react';
import { Artifact, ExportFormat } from '../../types/artifact';
import * as artifactService from '../../services/artifactService';
import { useTheme } from '../../contexts/ThemeContext';

interface ArtifactExportProps {
  artifact: Artifact;
}

export const ArtifactExport: React.FC<ArtifactExportProps> = ({ artifact }) => {
  const { darkMode } = useTheme();
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat | null>(null);
  const [exportResult, setExportResult] = useState<{ success: boolean; message?: string } | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const getAvailableFormats = (): ExportFormat[] => {
    // Common formats for all artifact types
    const formats: ExportFormat[] = [ExportFormat.JSON];
    
    // Add type-specific formats
    switch (artifact.type) {
      case 'text':
        formats.push(ExportFormat.MARKDOWN, ExportFormat.PDF, ExportFormat.HTML);
        break;
      case 'code':
        formats.push(ExportFormat.ZIP);
        break;
      case 'image':
        formats.push(ExportFormat.PNG, ExportFormat.SVG);
        break;
      case 'document':
        formats.push(ExportFormat.PDF, ExportFormat.MARKDOWN, ExportFormat.HTML, ExportFormat.DOCX);
        break;
      case 'diagram':
        formats.push(ExportFormat.SVG, ExportFormat.PNG);
        break;
      case 'solution':
      case 'idea':
      case 'critique':
        formats.push(ExportFormat.MARKDOWN, ExportFormat.PDF, ExportFormat.HTML);
        break;
    }
    
    return formats;
  };
  
  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setExportFormat(format);
    setExportResult(null);
    
    try {
      const result = await artifactService.exportArtifact(artifact.id, format);
      setExportResult(result);
      
      if (result.success) {
        if (result.url) {
          const a = document.createElement('a');
          a.href = result.url;
          if (result.filename) {
            a.download = result.filename;
          }
          a.target = '_blank';
          a.click();
        } else if (result.content) {
          navigator.clipboard.writeText(result.content);
          setExportResult({
            success: true,
            message: `Exported as ${format} and copied to clipboard`
          });
        }
      }
    } catch (error) {
      setExportResult({ 
        success: false, 
        message: `Failed to export: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  const formats = getAvailableFormats();

  return (
    <div className="artifact-export relative" ref={dropdownRef}>
      <div className="relative inline-block text-left">
        <button
          type="button"
          className={`
            inline-flex justify-center items-center w-full rounded-md px-4 py-2 text-sm font-medium
            shadow-md hover:shadow-lg transition-all duration-200
            ${darkMode
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
              : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          onClick={() => setDropdownOpen(prev => !prev)}
          disabled={isExporting}
        >
          {isExporting ? `Exporting as ${exportFormat}...` : 'Export'}
          <svg className="-mr-1 ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {dropdownOpen && (
          <div
            className={`origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-xl border z-20 ${
              darkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}
            role="menu"
            aria-orientation="vertical"
            tabIndex={-1}
          >
            <div className="py-1" role="none">
              {formats.map(format => (
                <button
                  key={format}
                  className={`block w-full text-left px-4 py-2 text-sm ${
                    darkMode
                      ? 'text-gray-200 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => {
                    handleExport(format);
                    setDropdownOpen(false);
                  }}
                >
                  Export as {format.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {exportResult && (
        <div className={`mt-2 p-2 text-sm rounded border ${
          exportResult.success
            ? darkMode
              ? 'bg-green-900/30 border-green-700 text-green-400'
              : 'bg-green-100 border-green-300 text-green-700'
            : darkMode
              ? 'bg-red-900/30 border-red-700 text-red-400'
              : 'bg-red-100 border-red-300 text-red-700'
        }`}>
          {exportResult.message}
        </div>
      )}
    </div>
  );
};
